import * as React from "react";

interface UseAnimatedNumberOptions {
	/** Animation duration in ms. Default 600. */
	duration?: number;
	/**
	 * Skip animation and snap to target. Useful when the consumer is driving
	 * rapid updates (e.g. cursor scrubbing) and wants instant response.
	 */
	immediate?: boolean;
	/**
	 * Start the initial value at 0 and animate to target on mount. Useful
	 * for hero figures that should "tick up" the first time they appear.
	 */
	animateOnMount?: boolean;
}

/**
 * Smoothly interpolates a numeric value when its target changes. Uses
 * requestAnimationFrame with ease-out-quart for natural deceleration.
 * Respects `prefers-reduced-motion` (snaps to target).
 */
export function useAnimatedNumber(
	target: number,
	{
		duration = 600,
		immediate = false,
		animateOnMount = false,
	}: UseAnimatedNumberOptions = {},
): number {
	const initial = animateOnMount ? 0 : target;
	const [current, setCurrent] = React.useState(initial);
	const fromRef = React.useRef(initial);
	const rafRef = React.useRef<number | null>(null);
	const targetRef = React.useRef(target);
	targetRef.current = target;

	// biome-ignore lint/correctness/useExhaustiveDependencies: `current` is captured as the start-of-animation snapshot via fromRef, not a real dep. Adding it would re-run the effect every frame.
	React.useEffect(() => {
		const prefersReduced =
			typeof window !== "undefined" &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		if (immediate || prefersReduced) {
			setCurrent(target);
			fromRef.current = target;
			return;
		}

		// Capture starting point at the moment the target changes
		fromRef.current = current;
		const start = performance.now();

		const tick = (now: number) => {
			// If the target shifted again mid-animation, snap from-current
			// and let the next effect run handle the new target.
			const t = Math.min((now - start) / duration, 1);
			const eased = 1 - (1 - t) ** 4; // ease-out-quart
			const value =
				fromRef.current + (targetRef.current - fromRef.current) * eased;
			setCurrent(value);
			if (t < 1) {
				rafRef.current = requestAnimationFrame(tick);
			} else {
				rafRef.current = null;
			}
		};

		rafRef.current = requestAnimationFrame(tick);
		return () => {
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
		};
	}, [target, duration, immediate]);

	return current;
}
